import { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config';

function getGuildId() {
  const session = localStorage.getItem('session');
  if (!session) return null;
  try {
    const payload = session.split('.')[1];
    const padding = 4 - payload.length % 4;
    const padded = padding !== 4 ? payload + '='.repeat(padding) : payload;
    const data = JSON.parse(atob(padded));
    // Standalone mode: use active_room from localStorage
    if (data.mode === 'standalone') return localStorage.getItem('active_room') || null;
    return data.guild_id || null;
  } catch { return null; }
}

export function useGameState() {
  const [players, setPlayers] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [activeTurn, setActiveTurn] = useState('');
  const [lastAttack, setLastAttack] = useState({});
  const [zones, setZones] = useState({});
  const [banner, setBanner] = useState('');
  const [channels, setChannels] = useState([]);
  const [pinnedChannels, setPinnedChannels] = useState([]);
  const [combatSorting, setCombatSorting] = useState(true);
  const [campaignNotes, setCampaignNotes] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [combatLog, setCombatLog] = useState([]);
  const [combatLoot, setCombatLoot] = useState(null);
  const [combatActive, setCombatActive] = useState(false);
  const [polledPlan, setPolledPlan] = useState(null);
  const [userRole, setUserRole] = useState('dm');
  const lastPlayerSig = useRef('');
  const lastEnemySig = useRef('');
  const lastAttackTs = useRef(0);
  const lastZoneSig = useRef('');
  const lastLogLen = useRef(0);

  useEffect(() => {
    const poll = async () => {
      const session = localStorage.getItem('session');
      if (!session) return;
      try {
        const guildId = getGuildId();
        if (!guildId) return;
        const resp = await fetch(`${API_BASE}/state/${guildId}`, {
          headers: { 'Authorization': 'Bearer ' + session }
        });
        if (!resp.ok) return;
        const data = await resp.json();

        // Update players only if changed
        const playerSig = JSON.stringify(data.players) + (data.active_turn || '');
        if (playerSig !== lastPlayerSig.current) {
          lastPlayerSig.current = playerSig;
          const sorted = (data.players || []).sort((a, b) => (b.active || 0) - (a.active || 0));
          setPlayers(sorted);
          setActiveTurn(data.active_turn || '');
        }

        // Update enemies only if changed (and only if non-empty or explicitly cleared)
        const enemySig = JSON.stringify(data.enemies || []);
        if (enemySig !== lastEnemySig.current) {
          lastEnemySig.current = enemySig;
          setEnemies(data.enemies || []);
        }

        // Update last attack only if new timestamp
        const atk = data.last_attack || {};
        if (atk.ts && atk.ts > lastAttackTs.current) {
          lastAttackTs.current = atk.ts;
          setLastAttack({ ...atk });
        }

        // Update zones
        const zoneSig = JSON.stringify(data.zones || {});
        if (zoneSig !== lastZoneSig.current) {
          lastZoneSig.current = zoneSig;
          setZones(data.zones || {});
        }

        // Update banner
        setBanner(data.banner || '');

        // Update channels
        if (data.channels && data.channels.length) setChannels(data.channels);
        if (data.pinned_channels) setPinnedChannels(data.pinned_channels);

        // Update combat sorting
        if (data.combat_sorting !== undefined) setCombatSorting(data.combat_sorting);

        // Update campaign notes
        if (data.campaign_notes !== undefined) setCampaignNotes(data.campaign_notes);
        if (data.plan) setPolledPlan(data.plan);
        if (data.user_role) setUserRole(data.user_role);
        if (data.invite_code) window._roomInviteCode = data.invite_code;

        // Combat log — only add NEW entries (skip history on first load)
        if (data.combat_log) {
          if (lastLogLen.current === 0 && data.combat_log.length > 0) {
            // First load — skip history, just set the baseline
            lastLogLen.current = data.combat_log.length;
          } else if (data.combat_log.length > lastLogLen.current) {
            const newEntries = data.combat_log.slice(lastLogLen.current);
            setCombatLog(prev => [...prev, ...newEntries]);
            lastLogLen.current = data.combat_log.length;
          }
        }
        // Reset log when combat starts fresh
        if (data.combat_active && !combatActive) {
          setCombatLog(data.combat_log || []);
          lastLogLen.current = (data.combat_log || []).length;
        }
        setCombatActive(data.combat_active || false);
        // Combat state tracking
        setCombatActive(data.combat_active || false);
        // Per-user loot notification — disabled (using command response only)
        // if (data.loot_notification) { ... }
        setInitialLoading(false);
      } catch (e) { setInitialLoading(false); }
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, []);

  return { players, enemies, activeTurn, lastAttack, zones, banner, channels, pinnedChannels, combatSorting, campaignNotes, initialLoading, combatLog, combatLoot, setCombatLoot, combatActive, polledPlan, userRole };
}
